import './SignUpEditPage.css'
import { useEffect, useRef, useState } from 'react'
import { AlertMessage, DcsPermissions, IParamsType, IRootState } from 'types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
    DefaultButton,
    SaveButton,
    StickyHeaderWrapper,
    MessageAlert,
    SideNav,
    SideNavContent,
    SideNavMenu,
    SideNavMenuItem,
    SideNavMenuSection,
    SideNavWrapper,
} from 'components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Form, FormElement } from '@progress/kendo-react-form'
import { Badge, Col, Row } from 'react-bootstrap'
import { DEFAULT_PERMISSION_MESSAGE, LS_SIDEBAR_MENU_STATE } from 'core/consts'
import { isMobile } from 'react-device-detect'
import { focusFirstError, getErrors } from 'core/helpers'
import { DealerDto } from '@v1DealersApi'
import { Switch, TextArea, TextBox } from '@progress/kendo-react-inputs'
import { Label } from '@progress/kendo-react-labels'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { DatePicker } from '@progress/kendo-react-dateinputs'

export enum EditSignUpSectionTypeEnum {
    LEAD_INFORMATION = 'lead-information',
    LOGIN_INFORMATION = 'login-information',
    PRODUCT_INFORMATION = 'product-information',
    PRIMARY_PRODUCT = 'primary-product',
    RECURRING_UPSELL = 'recurring-upsell',
    ONETIME_UPSELL = 'one-time-upsell',
    NOTES = 'notes',
    ACCOUNTING = 'accounting',
    DATA = 'data',
    SETUP = 'setup',
    WEB = 'web',
    DOMAIN = 'domain',
    DEALER_GROUP = 'dealer-group',
    BILLING_INFORMATION = 'billing-information',
    BILLING = 'billing-info',
    INITIAL_PAYMENT = 'initial-payment',
    RECURRING_PAYMENT = 'recurring-payment',
    ORDER_STATUS = 'order-status'
}

export interface IParams extends IParamsType {
    id: string
    dealerId: string
    section: string
}

const SignUpEditPage = () => {
    const { id, section } = useParams<IParams>()
    const navigate = useNavigate()

    const location = useLocation()
    const widthRef = useRef(window.innerWidth)
    const baseUrl = '/signup/edit'
    const loading = false ///useSelector((state: IRootState) => state.app.loading)
    const [dealer, setDealer] = useState<DealerDto>()

    const [showSideNavMenu, setShowSideNavMenu] = useState<boolean>(true)
    const [selectedSection, setSelectedSection] = useState<EditSignUpSectionTypeEnum>(
        section ? section as EditSignUpSectionTypeEnum : EditSignUpSectionTypeEnum.LEAD_INFORMATION
    )
    const [alert, setAlert] = useState<AlertMessage>()
    const [formIsModified, setFormIsModified] = useState<boolean>(false)

    //when the form is modified this is the dto that will be saved when the user clicks save in the save or discard modal
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [dtoToSave, setDtoToSave] = useState<any>()

    useEffect(() => {
        const storedSidebarStatus = localStorage.getItem(LS_SIDEBAR_MENU_STATE)
        if (storedSidebarStatus)
            setShowSideNavMenu(storedSidebarStatus === 'true')
    }, [])

    ///close sidebar on specific widths
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth === widthRef.current)
                return

            if (window.innerWidth < 1325) {
                setShowSideNavMenu(false)
                localStorage.setItem(LS_SIDEBAR_MENU_STATE, 'false')

            } else {
                setShowSideNavMenu(true)
                localStorage.setItem(LS_SIDEBAR_MENU_STATE, 'true')
            }

            widthRef.current = window.innerWidth
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])


    useEffect(() => {

    }, [])

    const changeSection = (sectionType: EditSignUpSectionTypeEnum) => {
        if (isMobile) {
            setShowSideNavMenu(false)
        }

        if (selectedSection === sectionType)
            return

        //if form is not modified just change section and update url
        if (!formIsModified) {
            setSelectedSection(sectionType)
            //special redirect that doesn't cause page render with side nav
            window.history.replaceState(null, '', `${baseUrl}/${sectionType}`)
            window.scrollTo(0, 0)
            return
        }
        window.scrollTo(0, 0)
    }


    // Temp dropdown data
    const salespeople = ['Salesperson 1', 'Salesperson 2', 'Salesperson 3']
    const countries = ['USA', 'Canada']
    const titles = ['Dealer Principal', 'General Manager', 'General Sales Manager']
    const dealertype = ['Independent', 'Franchise']
    const promotion = ['None', '$100 DCS', '$150 DCS']
    const packageselection = ['$80 DCS (Contract)', '$100 DCS (Contract)', '$125 DCS (Contract)']
    const monthsfree = ['0 Months Free', '1 Months Free']
    const upselldata = ['None', '$250 Per Month (Contract)', '$300 Per Month (Contract)']

    // Form Sections
    const LeadInformationForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='LeadInformationForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Lead Information</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={12}>
                                    <Label>Returning Dealer?</Label>{' '}
                                    <Switch onLabel='Yes' offLabel='No' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Salesperson:</Label>
                                    <DropDownList
                                        data={salespeople}
                                        defaultValue='Salesperson 1'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Company Name:</Label>
                                    <TextBox name='companyname' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Dealer Principal First Name:</Label>
                                    <TextBox name='dealerprincipalfirstname' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Dealer Principal Last Name:</Label>
                                    <TextBox name='dealerprincipallastname' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Principal Email:</Label>
                                    <TextBox name='principalemail' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Principal Phone:</Label>
                                    <TextBox name='principalphone' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Primary Contact First Name:</Label>
                                    <TextBox name='primarycontactfirstname' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Primary Contact Last Name:</Label>
                                    <TextBox name='primarycontactlastname' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Primary Email:</Label>
                                    <TextBox name='primaryemail' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Address 1:</Label>
                                    <TextBox name='address1' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Address 2:</Label>
                                    <TextBox name='address2' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>City:</Label>
                                    <TextBox name='city' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>State:</Label>
                                    <TextBox name='state' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Zip:</Label>
                                    <TextBox name='zip' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>City:</Label>
                                    <TextBox name='city' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Country:</Label>
                                    <DropDownList
                                        data={countries}
                                        defaultValue='USA'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Phone:</Label>
                                    <TextBox name='phone' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Fax:</Label>
                                    <TextBox name='fax' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Mobile Phone:</Label>
                                    <TextBox name='mobilephone' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Toll Free Phone:</Label>
                                    <TextBox name='tollfreephone' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website:</Label>
                                    <TextBox name='website' className='form-control' type='text' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }

    const LoginInformationForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='LoginInformationForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Login Information</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col lg={3} md={4}>
                                    <Label>Email Address:</Label>
                                    <TextBox name='emailaddress' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Username:</Label>
                                    <TextBox name='username' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Password:</Label>
                                    <TextBox name='password' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Mobile Phone (for 2FA):</Label>
                                    <TextBox name='mobilephone' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Title:</Label>
                                    <DropDownList
                                        data={titles}
                                        defaultValue='Dealer Principal'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Setup and Training Contact:</Label>
                                    <TextBox name='trainingcontact' className='form-control' type='text' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Trainee's Phone Number:</Label>
                                    <TextBox name='traineesphone' className='form-control' type='text' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const PrimaryProductForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='PrimaryProductForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Primary Product</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col md={6}>
                                    <Label>Dealer Type:</Label>
                                    <DropDownList
                                        data={dealertype}
                                        defaultValue='Independent'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Promotion:</Label>
                                    <DropDownList
                                        data={promotion}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Package Selection:</Label>
                                    <DropDownList
                                        data={packageselection}
                                        defaultValue='$100 DCS (Contract)'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label># of Months Free:</Label>
                                    <DropDownList
                                        data={monthsfree}
                                        defaultValue='0 Months Free'
                                        className='form-control'
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const RecurringUpsellForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='RecurringUpsellForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Recurring Upsell Product</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col md={6} className='mb-3 mb-md-0'>
                                    <div className='bg-white p-3 h-100' style={{ border: '2px dotted rgba(0, 0, 0, .125)' }}>
                                        <div className='d-flex'>
                                            <div className='p-2 me-auto'><b>Total Initial Charge</b></div>
                                            <div className='p-2 ms-auto'><b>$250</b></div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6} className='mb-3 mb-md-0'>
                                    <div className='bg-white p-3 h-100' style={{ border: '2px dotted rgba(0, 0, 0, .125)' }}>
                                        <div className='d-flex'>
                                            <div className='p-2 me-auto'><b>Total Recurring Charge</b></div>
                                            <div className='p-2 ms-auto'><b>$250</b></div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={3} md={4}>
                                    <Label>Frazer:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Vehicle Evaluator:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Texting:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Enhanced Messaging:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Call Tracking:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>CRM:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Digital Retailing:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Price Watch Notifications:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>AI Vehicle Description:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Reputation Management:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>FB Messenger:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>DCS Spin 360:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Blackbook Values:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Blackbook Decode & Values:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Blackbook Value My Trade:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>High Res Photos:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Image Enhancements:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>We Buy:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Chrome Factory Incentives:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Chrome Vin Decoder:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Automated Email Marketing:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Dealer Group Technology:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>SEO:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>CAC:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Monroney Labels:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Digital Accessibility:</Label>
                                    <DropDownList
                                        data={upselldata}
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Monthly Polling Fee:</Label>
                                    <DropDownList
                                        name='monthlypollingfee'
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const OneTimeUpsellForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='OneTimeUpsellForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>One Time Upsell Product</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={9}>
                                    <Label>Responsive Website Fee:</Label>
                                    <DropDownList
                                        defaultValue='$0 Complimentary'
                                        className='form-control'
                                    />
                                </Col>
                                <Col xs={3}>
                                    <Label>Hours:</Label>
                                    <TextBox name='responsivewebsitehours' className='form-control' type='text' />
                                </Col>
                                <Col xs={9}>
                                    <Label>Custom Web Hours:</Label>
                                    <DropDownList
                                        defaultValue='$0 Complimentary'
                                        className='form-control'
                                    />
                                </Col>
                                <Col xs={3}>
                                    <Label>Hours:</Label>
                                    <TextBox name='customwebhours' className='form-control' type='text' />
                                </Col>
                                <Col md={6}>
                                    <Label>Polling Setup Fee:</Label>
                                    <DropDownList
                                        name='pollingsetupfee'
                                        defaultValue='None'
                                        className='form-control'
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }

    const AccountingForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='AccountingForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Accounting</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col md={6}>
                                    <Label>Accounting Contact First Name:</Label>
                                    <TextBox name='accFirstName' className='form-control' type='text' />
                                </Col>
                                <Col md={6}>
                                    <Label>Accounting Contact Last Name:</Label>
                                    <TextBox name='accLastName' className='form-control' type='text' />
                                </Col>
                                <Col md={6}>
                                    <Label>Accounting Contact Email Address:</Label>
                                    <TextBox name='accEmail' className='form-control' type='text' />
                                </Col>
                                <Col md={6}>
                                    <Label>Accounting Contact Phone:</Label>
                                    <TextBox name='accPhone' className='form-control' type='text' />
                                </Col>
                                <Col md={12}>
                                    <Label>Accounting Notes:</Label>
                                    <TextArea rows={7} name='accNotes' className='form-control' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const DataForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='DataForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Data</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={12}>
                                    <b>Import Information:</b>
                                </Col>
                                <Col md={6} className='mb-0 mb-md-3'>
                                    <Label>One Time Import:</Label><br />
                                    <Switch offLabel='No' onLabel='Yes' />
                                </Col>
                                <Col md={6} className='mb-0 mb-md-3'>
                                    <Label>Recurring Import:</Label><br />
                                    <Switch offLabel='No' onLabel='Yes' />
                                </Col>
                                <Col xs={12} className='mt-3 mt-md-0'><b>Import/Export Notes:</b><br /></Col>
                                <Col md={6}>
                                    <Label>Dealership DMS Contact:</Label>
                                    <TextBox name='dmscontact' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Dealership DMS Contact Phone:</Label>
                                    <TextBox name='dmscontact' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Dealership Data QA Contact:</Label>
                                    <TextBox name='dmscontact' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Dealership QA Contact Phone:</Label>
                                    <TextBox name='dmscontact' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Dealership Data Manager Contact:</Label>
                                    <TextBox name='dmscontact' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Dealership Data Manager Phone</Label>
                                    <TextBox name='dmscontact' className='form-control' />
                                </Col>
                                <Col md={12}>
                                    <Label>Data Team Notes:</Label>
                                    <TextArea rows={7} name='setupteamnotes' className='form-control' />
                                </Col>
                                <Col xs={12} className='mt-3'>
                                    <b>Choose from 468 Exporters:</b>
                                </Col>
                                <Col xs={12}>
                                    <ul className='row list-unstyled'>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>14 K Business Solutions LLC:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>ChicagoLandAuto.com:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>LotLinx:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>1 Click:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>Chroma Cars - USAAutoPlex:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>LotVantage (BlueSkyMktng, SmartPillars, VueMedia):</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>1Micro:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>Chrysler DMI:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>LVAutoGuide_Com:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>360VinSpin:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>ClassicCars.com (shared inv with Hemmings.com):</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>Makatary:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>5 Star Auto Auction:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>ClassifiedPros.com:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>makeiteasyautos.com ( MakeItEasyAutos_Com):</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>5miles:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>Click Here Digital:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                        <li className='col-md-6 col-lg-4'>
                                            <Label>MAX Digital/FirstLook:</Label><br />
                                            <Switch offLabel='No' onLabel='Yes' />
                                        </li>
                                    </ul>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const SetupForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='SetupForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Setup</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col md={12}>
                                    <Label>Setup Team Notes:</Label>
                                    <TextArea rows={7} name='setupteamnotes' className='form-control' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const WebForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='WebForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Web</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={12}>
                                    <Label>Buy Here Pay Here:</Label>{' '}
                                    <Switch offLabel='No' onLabel='Yes' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Style:</Label>
                                    <DropDownList
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>List Module Style:</Label>
                                    <DropDownList
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Detail Module Style:</Label>
                                    <DropDownList
                                        className='form-control'
                                    />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Display Name:</Label>
                                    <TextBox name='websitedisplayname' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Phone:</Label>
                                    <TextBox name='websitephone' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Image 1:</Label>
                                    <TextBox name='websiteimage1' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Image 2:</Label>
                                    <TextBox name='websiteimage2' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Image 3:</Label>
                                    <TextBox name='websiteimage3' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Image 4:</Label>
                                    <TextBox name='websiteimage4' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Image 5:</Label>
                                    <TextBox name='websiteimage5' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Website Color Choices:</Label>
                                    <TextBox name='websitecolorchoices' className='form-control' />
                                </Col>
                                <Col lg={3} md={4}>
                                    <Label>Company Slogan:</Label>
                                    <TextBox name='websitecompanyslogan' className='form-control' />
                                </Col>
                                <Col xs={12}>
                                    <Label>Web Team Notes:</Label>
                                    <TextArea rows={7} name='websiteteamnotes' className='form-control' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const DomainForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='DomainForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Domain</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col md={6}>
                                    <Label>1st Domain Choice:</Label>
                                    <TextBox name='domain1stchoice' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Existing Domain Name:</Label>
                                    <TextBox name='existingdomainname' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Domain Provider:</Label>
                                    <DropDownList
                                        name='domainprovider'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Domain Username:</Label>
                                    <TextBox name='domainusername' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Domain Password:</Label>
                                    <TextBox name='domainpassword' className='form-control' />
                                </Col>

                                <Col xs={12}>
                                    <Label>Domain Notes:</Label>
                                    <TextArea rows={7} name='domainnotes' className='form-control' />
                                </Col>
                                <Col xs={12}>
                                    <a href='https://www.secureserver.net/domains/search.aspx?ci=14620&prog_id=domainspricedright&pl_id=1592' target='_blank' className='btn btn-default me-2 mt-3'>Domain Lookup</a>
                                    <a href='https://www.whois.com/whois' target='_blank' className='btn btn-default mt-3'>Whois Lookup</a>
                                    <div className='bg-white p-3 border shadow-sm mt-3'>
                                        <FontAwesomeIcon icon={'info-circle'} /> {' '}If someone is preventing you from full access to a domain that identifies your business or uses your brand names, you should be aware that under certain circumstances, such activity may be unlawful under the Anticybersquatting Consumer Protection Act (ACPA). The ACPA allows for actions to recover domains, and potentially to recover damages up to $100,000 per domain, against persons who use protectable domains for their own profit in bad faith. Dealer Car Search cannot provide legal assistance, so you may want to consider seeking the advice of an attorney with such issues. Alternatively, you may determine that you would rather select a new domain name, including, if appropriate, one that might be very similar to your old domain (e.g., add or remove hyphens).
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const DealerGroupForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='DealerGroupForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Dealer Group</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={12}>
                                    <Label>Dealer Group:</Label>{' '}
                                    <div className='form-check form-check-inline'>
                                        <label className='form-check-label cursor-pointer'>
                                            <input className='form-check-input' type='radio' name='flexRadioDefault' id='flexRadioDefault1' />
                                            New
                                        </label>
                                    </div>
                                    <div className='form-check form-check-inline'>
                                        <label className='form-check-label cursor-pointer'>
                                            <input className='form-check-input' type='radio' name='flexRadioDefault' id='flexRadioDefault2' />
                                            Existing
                                        </label>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <Label>New Dealer Group Name:</Label>
                                    <TextBox name='dealergroupname' className='form-control' />
                                </Col>
                                <Col md={4}>
                                    <Label>Dealers in the Group:</Label>
                                    <TextBox name='dealerinthegroup' className='form-control' />
                                </Col>
                                <Col md={4}>
                                    <Label>Splash Page:</Label>
                                    <DropDownList
                                        name='splashpage'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Dealer Group Inv. on Splash Page:</Label><br />
                                    <Switch offLabel='No' onLabel='Yes' />
                                </Col>
                                <Col md={4}>
                                    <Label>Dealer Group Inv. on All Sites:</Label><br />
                                    <Switch offLabel='No' onLabel='Yes' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }


    const BillingInformationForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='BillingInformationForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Billing Information</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={12}>
                                    <Label>Billing Address:</Label>{' '}
                                    <div className='form-check form-check-inline'>
                                        <label className='form-check-label cursor-pointer'>
                                            <input className='form-check-input' type='radio' name='flexRadioDefault' id='flexRadioDefault5' />
                                            Same as Dealership Address
                                        </label>
                                    </div>
                                    <div className='form-check form-check-inline'>
                                        <label className='form-check-label cursor-pointer'>
                                            <input className='form-check-input' type='radio' name='flexRadioDefault' id='flexRadioDefault6' />
                                            Same as Recurring Billing Address
                                        </label>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <Label>Bill To:</Label>
                                    <TextBox name='billto' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Address 1:</Label>
                                    <TextBox name='address1' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Address 2:</Label>
                                    <TextBox name='address2' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>City:</Label>
                                    <TextBox name='billto' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>State:</Label>
                                    <DropDownList
                                        name='state'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Zip:</Label>
                                    <TextBox name='zip' className='form-control' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const InitialPaymentForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='InitialPaymentForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Initial Payment</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={12}>
                                    <Label>Billing Address:</Label>{' '}
                                    <div className='form-check form-check-inline'>
                                        <label className='form-check-label cursor-pointer'>
                                            <input className='form-check-input' type='radio' name='flexRadioDefault' id='flexRadioDefault7' />
                                            Same as Dealership Address
                                        </label>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <Label>Payment Method:</Label>
                                    <DropDownList
                                        name='paymentmethod'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Address 1:</Label>
                                    <TextBox name='address1' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Address 2:</Label>
                                    <TextBox name='address2' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>City:</Label>
                                    <TextBox name='billto' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>State:</Label>
                                    <DropDownList
                                        name='state'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Zip:</Label>
                                    <TextBox name='zip' className='form-control' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }
    const RecurringPaymentForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='RecurringPaymentForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Recurring Payment</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col xs={12}>
                                    <Label>Billing Address:</Label>{' '}
                                    <div className='form-check form-check-inline'>
                                        <label className='form-check-label cursor-pointer'>
                                            <input className='form-check-input' type='radio' name='flexRadioDefault' id='flexRadioDefault8' />
                                            Same as Initial Payment
                                        </label>
                                    </div>
                                    <div className='form-check form-check-inline'>
                                        <label className='form-check-label cursor-pointer'>
                                            <input className='form-check-input' type='radio' name='flexRadioDefault' id='flexRadioDefault9' />
                                            Same as Dealership Address
                                        </label>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <Label>Payment Method:</Label>
                                    <DropDownList
                                        name='paymentmethod'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Address 1:</Label>
                                    <TextBox name='address1' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>Address 2:</Label>
                                    <TextBox name='address2' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>City:</Label>
                                    <TextBox name='billto' className='form-control' />
                                </Col>
                                <Col md={6}>
                                    <Label>State:</Label>
                                    <DropDownList
                                        name='splashpage'
                                        className='form-control'
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Zip:</Label>
                                    <TextBox name='zip' className='form-control' />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }

    const OrderStatusForm = () => {
        return <Form
            ignoreModified={true}
            render={(formRenderProps) => (
                <FormElement>
                    <div id='OrderStatusForm'>
                        <div className='bg-light shadow-sm p-3 border pb-5'>
                            <Row>
                                <Col md={6}><h4>Order Status</h4></Col>
                                <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                                    {/* place right aligned element here if needed */}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col md={6}>
                                    <Label>Demo Date:</Label>
                                    <DatePicker />
                                </Col>
                                <Col md={6}>
                                    <Label>Order Status:</Label>
                                    <DropDownList
                                        name='orderstatus'
                                        defaultValue='Ready'
                                        className='form-control'
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className='my-4'>
                        <Col xs={12} sm={4}>
                            <SaveButton
                                title={!editable ? DEFAULT_PERMISSION_MESSAGE : undefined}
                                onClick={() => focusFirstError(formRenderProps)}
                                type='submit' />
                        </Col>
                    </Row>
                </FormElement>
            )
            }
        />
    }

    const hasEditInventoryPermission = true// hasPermission(DcsPermissions.EditInventory)

    const editable = hasEditInventoryPermission

    const SectionHandler = (selectedStep: EditSignUpSectionTypeEnum) => {
        switch (selectedStep) {
            case EditSignUpSectionTypeEnum.LEAD_INFORMATION:
                return <LeadInformationForm />

            case EditSignUpSectionTypeEnum.LOGIN_INFORMATION:
                return <LoginInformationForm />

            case EditSignUpSectionTypeEnum.PRIMARY_PRODUCT:
                return <PrimaryProductForm />
            case EditSignUpSectionTypeEnum.RECURRING_UPSELL:
                return <RecurringUpsellForm />
            case EditSignUpSectionTypeEnum.ONETIME_UPSELL:
                return <OneTimeUpsellForm />

            case EditSignUpSectionTypeEnum.ACCOUNTING:
                return <AccountingForm />
            case EditSignUpSectionTypeEnum.DATA:
                return <DataForm />
            case EditSignUpSectionTypeEnum.SETUP:
                return <SetupForm />
            case EditSignUpSectionTypeEnum.WEB:
                return <WebForm />
            case EditSignUpSectionTypeEnum.DOMAIN:
                return <DomainForm />
            case EditSignUpSectionTypeEnum.DEALER_GROUP:
                return <DealerGroupForm />

            case EditSignUpSectionTypeEnum.BILLING_INFORMATION:
                return <BillingInformationForm />
            case EditSignUpSectionTypeEnum.INITIAL_PAYMENT:
                return <InitialPaymentForm />
            case EditSignUpSectionTypeEnum.RECURRING_PAYMENT:
                return <RecurringPaymentForm />

            case EditSignUpSectionTypeEnum.ORDER_STATUS:
                return <OrderStatusForm />
            default:
                return <LeadInformationForm />
        }
    }

    const sectionActive = (array: Array<EditSignUpSectionTypeEnum>) => {
        return array.some(a => a === selectedSection)
    }

    const itemActive = (item: EditSignUpSectionTypeEnum) => item === selectedSection

    return (
        <div id='SignUpEditPage'>
            <MessageAlert alert={alert} />

            <StickyHeaderWrapper>
                <div className='row p-2 mt-2'>
                    <div className='col-md-4' style={{ alignSelf: 'center' }}>
                        <span className='pageHeader'>
                            <h3>Sign Up - Edit</h3>
                        </span>
                    </div>
                    <div className='col-md-8 text-md-end text-start mt-2 mt-lg-0' style={{ alignSelf: 'center' }}>
                        <div>
                            {
                                formIsModified
                                    ? <i id='unsavedMessage' className='font-weight-bold me-1'><Badge bg='danger'><FontAwesomeIcon icon={['fas', 'exclamation-circle']} /> unsaved changes</Badge></i>
                                    : <></>
                            }
                            <DefaultButton
                                className='me-1 btn-sm'
                                variant='default'
                                disabled={loading}
                                onClick={() => navigate('#')}>
                                Generate Initial Quote
                            </DefaultButton>
                            <DefaultButton variant='default' className='btn-sm me-1'>Generate Recurring Quote</DefaultButton>
                            <SaveButton className='btn btn-sm' />
                        </div>
                    </div>
                </div>
                <div className='section-divider'></div>
            </StickyHeaderWrapper>

            <SideNavWrapper>
                <SideNav
                    showMenu={showSideNavMenu}>
                    <SideNavMenu>
                        <SideNavMenuItem
                            title='Lead Information'
                            active={selectedSection === EditSignUpSectionTypeEnum.LEAD_INFORMATION}
                            onClick={() => changeSection(EditSignUpSectionTypeEnum.LEAD_INFORMATION)}
                        />
                        <SideNavMenuItem
                            title='Login Information'
                            active={selectedSection === EditSignUpSectionTypeEnum.LOGIN_INFORMATION}
                            onClick={() => changeSection(EditSignUpSectionTypeEnum.LOGIN_INFORMATION)}
                        />

                        <SideNavMenuSection
                            title='Product Information'
                            active={sectionActive([
                                EditSignUpSectionTypeEnum.PRIMARY_PRODUCT,
                                EditSignUpSectionTypeEnum.RECURRING_UPSELL,
                                EditSignUpSectionTypeEnum.ONETIME_UPSELL
                            ])}>
                            <SideNavMenuItem
                                title='Primary Product'
                                active={itemActive(EditSignUpSectionTypeEnum.PRIMARY_PRODUCT)}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.PRIMARY_PRODUCT)} />
                            <SideNavMenuItem
                                title='Recurring Upsell'
                                active={selectedSection === EditSignUpSectionTypeEnum.RECURRING_UPSELL}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.RECURRING_UPSELL)} />
                            <SideNavMenuItem
                                title='One Time Upsell'
                                active={selectedSection === EditSignUpSectionTypeEnum.ONETIME_UPSELL}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.ONETIME_UPSELL)} />
                        </SideNavMenuSection>

                        <SideNavMenuSection
                            title='Notes/Setup'
                            active={sectionActive([
                                EditSignUpSectionTypeEnum.ACCOUNTING,
                                EditSignUpSectionTypeEnum.DATA,
                                EditSignUpSectionTypeEnum.SETUP,
                                EditSignUpSectionTypeEnum.WEB,
                                EditSignUpSectionTypeEnum.DOMAIN,
                                EditSignUpSectionTypeEnum.DEALER_GROUP,
                            ])}>
                            <SideNavMenuItem
                                title='Accounting'
                                active={itemActive(EditSignUpSectionTypeEnum.ACCOUNTING)}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.ACCOUNTING)} />
                            <SideNavMenuItem
                                title='Data'
                                active={selectedSection === EditSignUpSectionTypeEnum.DATA}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.DATA)} />
                            <SideNavMenuItem
                                title='Setup'
                                active={selectedSection === EditSignUpSectionTypeEnum.SETUP}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.SETUP)} />
                            <SideNavMenuItem
                                title='Web'
                                active={selectedSection === EditSignUpSectionTypeEnum.WEB}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.WEB)} />
                            <SideNavMenuItem
                                title='Domain'
                                active={selectedSection === EditSignUpSectionTypeEnum.DOMAIN}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.DOMAIN)} />
                            <SideNavMenuItem
                                title='Dealer Group'
                                active={selectedSection === EditSignUpSectionTypeEnum.DEALER_GROUP}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.DEALER_GROUP)} />
                        </SideNavMenuSection>

                        <SideNavMenuSection
                            title='Billing Information'
                            active={sectionActive([
                                EditSignUpSectionTypeEnum.BILLING_INFORMATION,
                                EditSignUpSectionTypeEnum.INITIAL_PAYMENT,
                                EditSignUpSectionTypeEnum.RECURRING_PAYMENT
                            ])}>
                            <SideNavMenuItem
                                title='Billing Info'
                                active={itemActive(EditSignUpSectionTypeEnum.BILLING_INFORMATION)}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.BILLING_INFORMATION)} />
                            <SideNavMenuItem
                                title='Initial Payment'
                                active={selectedSection === EditSignUpSectionTypeEnum.INITIAL_PAYMENT}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.INITIAL_PAYMENT)} />
                            <SideNavMenuItem
                                title='Recurring Payment'
                                active={selectedSection === EditSignUpSectionTypeEnum.RECURRING_PAYMENT}
                                onClick={() => changeSection(EditSignUpSectionTypeEnum.RECURRING_PAYMENT)} />
                        </SideNavMenuSection>

                        <SideNavMenuItem
                            title='Order Status'
                            active={selectedSection === EditSignUpSectionTypeEnum.ORDER_STATUS}
                            onClick={() => changeSection(EditSignUpSectionTypeEnum.ORDER_STATUS)}
                        />

                    </SideNavMenu>

                </SideNav>
                <SideNavContent showMenu={showSideNavMenu}
                    onToggleMenu={() => {
                        setShowSideNavMenu(!showSideNavMenu)
                        localStorage.setItem(LS_SIDEBAR_MENU_STATE, (!showSideNavMenu).toString())
                    }
                    }>
                    <div className={`ms-2 mb-4 ${showSideNavMenu ? 'shown' : 'hidden'}`}>
                        {SectionHandler(selectedSection)}
                    </div>
                </SideNavContent>
            </SideNavWrapper>
        </div>
    )
}
export default SignUpEditPage